import React from 'react';
import './index.scss';

/**
 * Reusable InputField component
 *
 * Props:
 *  label        - string, field label
 *  name         - string, input name
 *  type         - string, input type (text | email | tel | password | number | textarea | checkbox)
 *  value        - controlled value
 *  onChange     - change handler
 *  onBlur       - blur handler
 *  placeholder  - string
 *  error        - string, error message
 *  required     - bool
 *  disabled     - bool
 *  readOnly     - bool
 *  maxLength    - number
 *  rows         - number (textarea only)
 *  fullWidth    - bool, makes wrapper full-width in a grid
 *  className    - extra class on wrapper
 */
const InputField = ({
    label,
    name,
    type = 'text',
    value,
    onChange,
    onBlur,
    placeholder,
    error,
    required = false,
    disabled = false,
    readOnly = false,
    maxLength,
    rows = 3,
    fullWidth = false,
    className = '',
}) => {
    const wrapperClass = [
        'input-field-wrapper',
        fullWidth ? 'full-width' : '',
        disabled ? 'is-disabled' : '',
        error ? 'has-error' : '',
        className,
    ].filter(Boolean).join(' ');

    const inputClass = [
        'input-field',
        disabled ? 'disabled-field' : '',
        error ? 'input-error' : '',
    ].filter(Boolean).join(' ');

    return (
        <div className={wrapperClass}>
            {label && type !== 'checkbox' && (
                <label className="input-label" htmlFor={name}>
                    {label}
                    {required && <span className="required-mark"> *</span>}
                </label>
            )}

            {type === 'textarea' ? (
                <textarea
                    id={name}
                    name={name}
                    value={value}
                    onChange={onChange}
                    onBlur={onBlur}
                    placeholder={placeholder}
                    disabled={disabled}
                    readOnly={readOnly}
                    maxLength={maxLength}
                    rows={rows}
                    required={required}
                    className={inputClass}
                />
            ) : type === 'checkbox' ? (
                <label className="checkbox-label" htmlFor={name}>
                    <input
                        id={name}
                        type="checkbox"
                        name={name}
                        checked={!!value}
                        onChange={onChange}
                        onBlur={onBlur}
                        disabled={disabled}
                        className="checkbox-input"
                    />
                    <span className="checkbox-text">{label}</span>
                </label>
            ) : (
                <input
                    id={name}
                    type={type}
                    name={name}
                    value={value}
                    onChange={onChange}
                    onBlur={onBlur}
                    placeholder={placeholder}
                    disabled={disabled}
                    readOnly={readOnly}
                    maxLength={maxLength}
                    required={required}
                    className={inputClass}
                />
            )}

            {error && <span className="input-error-msg">{error}</span>}
        </div>
    );
};

export default InputField;
